const DEFAULT_TIME_ZONE = "Europe/Chisinau";
const SERVER_ERROR_PREFIX_RE = /^Server error(?::|\s)+/i;
const GENERIC_BATCH_CREATE_RE =
   /some reservations could not be created|unele program[aă]ri nu au putut fi create/i;

function pickErrorText(error, fallback = "") {
   if (!error) return String(fallback || "").trim();

   const direct =
      error?.error ??
      error?.message ??
      error?.data?.message ??
      error?.data?.error ??
      error;

   return String(direct || fallback || "").trim();
}

function cleanServerErrorPrefix(text) {
   return String(text || "")
      .replace(SERVER_ERROR_PREFIX_RE, "")
      .trim();
}

function tryParseJsonFromErrorText(text) {
   const raw = String(text || "").trim();
   if (!raw) return null;

   const directCandidates = [raw, cleanServerErrorPrefix(raw)];
   for (const candidate of directCandidates) {
      if (!candidate) continue;
      try {
         return JSON.parse(candidate);
      } catch {}
   }

   const jsonStart = raw.indexOf("{");
   if (jsonStart >= 0) {
      try {
         return JSON.parse(raw.slice(jsonStart));
      } catch {}
   }

   return null;
}

function formatLocalDateTime(dateLike, timeZone = DEFAULT_TIME_ZONE) {
   if (!dateLike) return "";

   const d = new Date(dateLike);
   if (Number.isNaN(d.getTime())) return "";

   try {
      return new Intl.DateTimeFormat("ro-RO", {
         timeZone,
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
         hour: "2-digit",
         minute: "2-digit",
         hour12: false,
      }).format(d);
   } catch {
      return d.toISOString().slice(0, 16).replace("T", " ");
   }
}

function normalizeBackendConflictMessage(message) {
   const raw = String(message || "").trim();
   if (!raw) return "";

   const lower = raw.toLowerCase();
   if (lower.includes("already reserved")) {
      return "Instructorul este deja ocupat la ora aleasa.";
   }
   if (lower.includes("could not be created")) {
      return "Unele programari nu au putut fi create.";
   }

   const noInstructorMatch = raw.match(
      /^No available\s+(.+?)\s+instructor(?:\s+for\s+(.+))?$/i,
   );
   if (noInstructorMatch) {
      const sectorRaw = String(noInstructorMatch[1] || "").trim();
      const sectorLower = sectorRaw.toLowerCase();
      const sector =
         sectorLower.includes("bot")
            ? "Botanica"
            : sectorLower.includes("cio")
              ? "Ciocana"
              : sectorLower.includes("bui")
                ? "Buiucani"
                : sectorRaw;
      return sector
         ? `Nu exista instructor disponibil in sectorul ${sector}.`
         : "Nu exista niciun instructor disponibil pentru slotul ales.";
   }

   return raw;
}

export function formatCalendarCreateError(error, options = {}) {
   const {
      fallback = "Programarea nu a putut fi creata. Verifica si incearca din nou.",
      timeZone = DEFAULT_TIME_ZONE,
   } = options;

   const rawText = pickErrorText(error, fallback);
   const parsed = tryParseJsonFromErrorText(rawText);

   if (parsed && typeof parsed === "object") {
      const reservationErrors = Array.isArray(parsed.reservationErrors)
         ? parsed.reservationErrors
         : [];
      const firstConflict = reservationErrors[0] || null;
      const backendMessage = normalizeBackendConflictMessage(
         firstConflict?.message || parsed.message,
      );
      const parsedMessage = String(parsed.message || "").trim();
      const detailedMessage = normalizeBackendConflictMessage(
         firstConflict?.message || "",
      );
      const localWhen = formatLocalDateTime(
         firstConflict?.reservation?.startTime || firstConflict?.startTime || null,
         timeZone,
      );
      const instructorId =
         firstConflict?.instructorId ??
         firstConflict?.reservation?.instructorId ??
         null;
      const isConflict =
         reservationErrors.length > 0 &&
         /already reserved|occupied|conflict/i.test(
            String(firstConflict?.message || parsed.message || ""),
         );

      if (isConflict) {
         const prefix =
            instructorId != null
               ? `Instructorul ${instructorId}`
               : "Instructorul";
         const whenText = localWhen ? ` la ${localWhen}` : "";
         const extraCount =
            reservationErrors.length > 1
               ? ` (${reservationErrors.length} conflicte)`
               : "";

         return {
            text: `${prefix} este deja ocupat${whenText}.${extraCount} Calendarul se reincarca automat.`,
            isConflict: true,
            parsed,
            rawText,
         };
      }

      const preferredMessage =
         reservationErrors.length > 0 &&
         (GENERIC_BATCH_CREATE_RE.test(parsedMessage) || !parsedMessage)
            ? detailedMessage || backendMessage || parsedMessage
            : parsedMessage || detailedMessage || backendMessage;
      return {
         text:
            cleanServerErrorPrefix(
               String(preferredMessage || rawText || fallback),
            ) || String(fallback),
         isConflict: false,
         parsed,
         rawText,
      };
   }

   const cleaned = cleanServerErrorPrefix(rawText) || String(fallback);
   const isConflict = /already reserved|occupied|conflict/i.test(cleaned);

   return {
      text:
         isConflict && cleaned === rawText
            ? "Slotul s-a ocupat intre timp. Calendarul se reincarca automat."
            : cleaned,
      isConflict,
      parsed: null,
      rawText,
   };
}
